import { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  useNoticeBannersStackContext,
  useSafeNavigation,
  useSafeRouteParams,
} from '@sg/garnish';
import { ChallengeStatus } from '@sg/graphql-schema';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useLoyaltyOfferOptInOnFocusMutation } from './graphql/LoyaltyOfferOptInOnFocus.generated';

// ─────────────────────────────────────────────────────────────────────────────

export function useOptInChallengeOnFocus() {
  // ─────────────────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const { formatMessage } = useIntl();

  // ─── Router Params ───────────────────────────────────────────────────

  const params = useSafeRouteParams<OptInChallengeParams>() ?? {};
  const navigation = useSafeNavigation();

  const [, optInChallenge] = useLoyaltyOfferOptInOnFocusMutation();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const { challengeId, optIn } = params;

  // ─── Opt In Callback ─────────────────────────────────────────────────

  const handleOptInChallenge = useCallback(async () => {
    if (!isLoggedIn) return;

    if (!challengeId || optIn !== 'true') return;

    navigation?.setParams({ challengeId: undefined, optIn: undefined });

    const { data } = await optInChallenge({ challengeId });

    const hasOptedIn =
      data?.challengeOptInV2?.__typename === 'Challenge' &&
      data?.challengeOptInV2?.status === ChallengeStatus.Started;

    // ─── Notice Banners ──────────────────────────────────────────

    if (hasOptedIn) {
      addNoticeBanner({
        palette: 'success',
        text: formatMessage(messages.optInSuccess),
      });

      return;
    }

    addNoticeBanner({
      palette: 'caution',
      text: formatMessage(messages.optInFailure),
    });
  }, [
    isLoggedIn,
    challengeId,
    optIn,
    navigation,
    optInChallenge,
    addNoticeBanner,
    formatMessage,
  ]);

  // ─── Entrypoint ──────────────────────────────────────────────────────

  useEffect(() => {
    void handleOptInChallenge();
  }, [handleOptInChallenge]);
}

// ─── Types ───────────────────────────────────────────────────────────────────

type OptInChallengeParams = {
  challengeId?: string;
  optIn?: string;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  optInSuccess: {
    defaultMessage: 'You have successfully opted in to this challenge',
    description: 'Loyalty | Opt-in challenge on focus | Success message',
  },
  optInFailure: {
    defaultMessage: 'An error occurred while opting in to this challenge',
    description: 'Loyalty | Opt-in challenge on focus | Failure message',
  },
});
